import { ClearTime, ClearTimePayload } from 'Interfaces/ReferenceInterfaces/ClearTimeInterface';
import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { clearTimeServices } from 'services/ReferenceScreenServices/clearTimeServices';

export class ClearTimeStore {
  clearTimes: ClearTime[] = [];
  isLoading: boolean = false;
  isPolling: boolean = false;
  pagination = {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrevious: false
  };
  selectedClearTime: ClearTime | null = null;
  environment = import.meta.env.VITE_ENVIRONMENT;

  constructor() {
    makeAutoObservable(this);
  }

  // Initial load method (with loading indicator)
  fetchClearTimes = async (page = 1, pageSize = 10000) => {
    this.isLoading = true;
    try {
      const response = await clearTimeServices.getAllClearTimes(page, pageSize);
      runInAction(() => {
        this.clearTimes = response.data;
        this.pagination = {
          currentPage: response.pagination.current_page,
          totalPages: response.pagination.total_pages,
          totalItems: response.pagination.total_items,
          hasNext: response.pagination.has_next,
          hasPrevious: response.pagination.has_previous
        };
      });
    } catch (error) {
      toast.error('Error fetching clear times');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // Polling method (without loading indicator)
  pollClearTimes = async (page = 1, pageSize = 10000) => {
    this.isPolling = true;
    try {
      const response = await clearTimeServices.getAllClearTimes(page, pageSize);
      runInAction(() => {
        // Update data only if it has changed
        if (JSON.stringify(this.clearTimes) !== JSON.stringify(response.data)) {
          this.clearTimes = response.data;
          this.pagination = {
            currentPage: response.pagination.current_page,
            totalPages: response.pagination.total_pages,
            totalItems: response.pagination.total_items,
            hasNext: response.pagination.has_next,
            hasPrevious: response.pagination.has_previous
          };
        }
      });
    } catch (error) {
      // Don't show toast for polling errors to avoid spam
      if (this.environment === 'development') {
        console.error('Error polling clear times:', error);
      }
      toast.error('Error polling clear times!');
    } finally {
      runInAction(() => {
        this.isPolling = false;
      });
    }
  };

  // Method to refresh data (can be called manually)
  refreshData = async (page = 1, pageSize = 10000) => {
    await this.fetchClearTimes(page, pageSize);
  };

  createClearTime = async (clearTimeData: ClearTimePayload): Promise<boolean> => {
    this.isLoading = true;
    try {
      const response = await clearTimeServices.createClearTime(clearTimeData);
      if (response.status) {
        toast.success('Clear time created successfully');
        await this.fetchClearTimes(this.pagination.currentPage);
        return true;
      } else {
        toast.error(response.message || 'Failed to create clear time');
        return false;
      }
    } catch (error) {
      toast.error('Error creating clear time');
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  updateClearTime = async (id: number, clearTimeData: ClearTimePayload): Promise<boolean> => {
    this.isLoading = true;
    try {
      const response = await clearTimeServices.updateClearTime(id, clearTimeData);
      if (response.status) {
        toast.success('Clear time updated successfully');
        await this.fetchClearTimes(this.pagination.currentPage);
        return true;
      } else {
        toast.error(response.message || 'Failed to update clear time');
        return false;
      }
    } catch (error) {
      toast.error('Error updating clear time');
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  deleteClearTime = async (id: number) => {
    this.isLoading = true;
    try {
      const response = await clearTimeServices.deleteClearTime(id);
      if (response.status) {
        toast.success('Clear time deleted successfully');

        const isLastItemOnPage = this.clearTimes.length === 1;
        const isLastPage = this.pagination.currentPage === this.pagination.totalPages;

        if (isLastItemOnPage && !isLastPage && this.pagination.currentPage > 1) {
          await this.fetchClearTimes(this.pagination.currentPage - 1);
        } else {
          await this.fetchClearTimes(this.pagination.currentPage);
        }
      } else {
        toast.error(response.message || 'Failed to delete clear time');
      }
    } catch (error) {
      toast.error('Error deleting clear time');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  setSelectedClearTime = (clearTime: ClearTime | null) => {
    this.selectedClearTime = clearTime;
  };
}

export const clearTimeStore = new ClearTimeStore();
